import {
    CurrencyEnum,
    Employee,
    Expense,
    ExpenseKindEnum,
    ExpenseSplit,
    ExtendedReferencedTax,
    Hospitality,
    InvoiceSplit,
    ItemSplit,
    OcrResult,
    SplitTypeEnum,
    TripFolder,
} from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { OcrResultItem } from "@finway-group/shared/lib/models/ocr/ocr.model"
import { UserLanguageEnum } from "@finway-group/shared/lib/models/user/userSettings.interface"
import { LabeledValue } from "antd/lib/select"
import moment from "moment"
import * as stringSimilarity from "string-similarity"

import { deleteUnnecessaryExpenseFormData, isHospitalityExpense } from "Features/pages/expenses/expenseUtils"
import { DE_DATE_FORMAT, NO_TAX_RATE, US_DATE_FORMAT } from "Shared/config/consts"
import { EmployeeService, NotificationService, TaxService, VendorService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { updateExpense } from "Shared/store/actions/expense/expenseActions"

import { confirmFolderAmountChange, getApprovalProcessesAfterExpenseUpdate } from "./approvalProcess.utils"
import { syncExpenseSplits } from "./expense.utils"
import { capitalizeFirstLetter, formatCurrencyNumber, isEmptyObject, isSet } from "./helpers"
import {
    BuildOcrUpdateDataParams,
    CompareDataParams,
    CompareFormOcrDataParams,
    CompareSplitsDataParams,
    FillFormWithOcrDataParams,
    MapSplitsParams,
    OcrObjectKeys,
    OcrObjectSplitsKeys,
    OnUpdateExpenseFromOcrParams,
    RowInterface,
    SplitDataType,
    UpdateOcrItemsParams,
    UpdatedOcrObject,
} from "./ocr.utils.types"
import { checkVendorSimilarity } from "./vendor.utils"

export const fillFormWithOcrData = async ({
    formInstance,
    expense,
    ocrObject,
    vendors,
    taxes,
    updateExpense,
    setPrefilledVendorData,
    t,
    ocrItemizationEnabled,
}: FillFormWithOcrDataParams) => {
    const isHospitality = isHospitalityExpense(expense)
    const requester = EmployeeService.getEmployeeById(expense.requestedBy._id)
    const formFields = formInstance.getFieldsValue()
    if (!formFields.paymentOption) formFields.paymentOption = expense.paymentOption
    if (!formFields.kind) formFields.kind = expense.kind
    if (!formFields.splitType) formFields.splitType = ocrItemizationEnabled ? SplitTypeEnum.ITEM : expense.splitType

    const updateData: UpdatedOcrObject = buildOcrUpdateData({ ocrObject, existingData: formFields, vendors, taxes, t, ocrItemizationEnabled, isHospitality, requester })

    if (ocrObject.vendor) setPrefilledVendorData({ ...ocrObject.vendor, description: t("info:filled_by_ocr") })

    updateExpense(updateData)
}

export const itemFieldsToBeCompared: Array<OcrObjectSplitsKeys> = ["name", "netPrice", "grossPrice", "taxRate", "taxPrice"]

const formatOCRObject = (ocrObject: OcrResult) => {
    const ocrObjectKeys = Object.keys(ocrObject) as Array<OcrObjectKeys>

    const newOcrObject = ocrObject as UpdatedOcrObject

    ocrObjectKeys.forEach((ocrKey) => {
        switch (ocrKey) {
            case "invoiceDate":
                newOcrObject[ocrKey] = newOcrObject[ocrKey] ? moment(newOcrObject[ocrKey]) : undefined
                break
            case "invoiceDueDate":
                newOcrObject[ocrKey] = moment(newOcrObject[ocrKey])
                break
            case "performancePeriod":
                newOcrObject[ocrKey] = newOcrObject[ocrKey]?.map((date) => moment(date))
                break
            case "datePurchased":
                newOcrObject[ocrKey] = moment(newOcrObject[ocrKey])
        }
    })

    return newOcrObject
}

export const buildOcrUpdateData = ({ ocrObject, existingData, vendors, taxes, t, ocrItemizationEnabled, isHospitality, requester }: BuildOcrUpdateDataParams): UpdatedOcrObject => {
    const { vendor } = ocrObject

    const foundVendor = vendor?.name && checkVendorSimilarity(vendors, vendor.name)

    const attendees: Array<LabeledValue> | undefined = isHospitality ? updatingAttendees(existingData, requester) : undefined

    const newOcrObject = {
        ...ocrObject,
        ...(isHospitality && attendees && { attendees }),
        ...(foundVendor && { vendor: { ...ocrObject.vendor, _id: foundVendor.id } }),
    }

    if (!foundVendor) {
        delete newOcrObject.vendor
    }

    const isAutoGeneratedCardRequest = existingData.isAutoGenerated && existingData.paymentOption === ExpensePaymentOptionEnum.SMART_CARD

    // For autoGenerated card requests we disregard the OCR total amount prices & splits
    const fieldsNotToBeUpdatedOnAutoGeneratedCardRequest = ["totalNetPrice", "totalGrossPrice", "totalTaxPrice", "splits", "taxRate"]

    if (isAutoGeneratedCardRequest) {
        for (const field of fieldsNotToBeUpdatedOnAutoGeneratedCardRequest) {
            delete newOcrObject[field as keyof OcrResult]
        }
        return formatOCRObject(newOcrObject)
    }

    const newTaxRate: ExtendedReferencedTax | undefined = ocrObject.taxRate ? getExpenseTaxRate(ocrObject.taxRate) : undefined

    const updatedOcrItems: SplitDataType =
        ocrObject.splits && ocrItemizationEnabled
            ? updateOcrItems({
                  ocrItemizationEnabled,
                  ocrObject: newOcrObject,
                  expense: existingData,
                  taxes,
                  t,
                  isHospitality,
              })
            : {
                  splits: existingData.splits,
              }

    const ocrObjectWithUpdatedSplits = { ...newOcrObject, ...(updatedOcrItems && { ...updatedOcrItems }) }

    const ocrObjectWithUpdatedTaxRate = {
        ...ocrObjectWithUpdatedSplits,
        ...(newTaxRate && { taxRate: newTaxRate }),
    }

    return formatOCRObject(ocrObjectWithUpdatedTaxRate)
}

const formatFieldValue = (field: OcrObjectKeys | OcrObjectSplitsKeys, value: any, currency: CurrencyEnum, userLanguage: UserLanguageEnum = UserLanguageEnum.DE) => {
    if (!isSet(value)) return ""

    switch (field) {
        case "invoiceNumber":
            return `#${value}`
        case "invoiceDate":
            return formatOcrDate(value, userLanguage)
        case "invoiceDueDate":
            return formatOcrDate(value, userLanguage)
        case "totalNetPrice":
            return formatCurrencyNumber(Number(value), currency)
        case "totalTaxPrice":
            return formatCurrencyNumber(Number(value), currency)
        case "totalGrossPrice":
            return formatCurrencyNumber(Number(value), currency)
        case "taxRate":
            if (!value || value._id === NO_TAX_RATE) return "-"
            return `${value.taxRate}%`
        case "taxPrice":
            return formatCurrencyNumber(Number(value), currency)
        case "grossPrice":
            return formatCurrencyNumber(Number(value), currency)
        case "netPrice":
            return formatCurrencyNumber(Number(value), currency)
        case "performancePeriod":
            if (value.includes(undefined) || !value.length) return "-"
            return `${formatOcrDate(value[0], userLanguage)} - ${formatOcrDate(value[1], userLanguage)}`
        case "datePurchased":
            return formatOcrDate(value, userLanguage)
        case "name":
            return capitalizeFirstLetter(value)
        case "vendor":
            return value.name
        default:
            return String(value)
    }
}

const formatOcrDate = (value: string | Date, userLanguage: UserLanguageEnum) => moment(value).format(userLanguage === UserLanguageEnum.DE ? DE_DATE_FORMAT : US_DATE_FORMAT)

const compareSplits = ({ originalSplits, ocrItems, t, newCurrency, originalCurrency }: MapSplitsParams): Array<RowInterface> => {
    const ocrItemsNames = ocrItems.map((item: ItemSplit) => item.name?.toLowerCase())

    const splitsData: Array<RowInterface> = originalSplits.map((originalSplit, itemIndex) => {
        let item: RowInterface = {} as RowInterface

        const ocrBestMatching = ocrItemsNames.length && "name" in originalSplit ? stringSimilarity.findBestMatch(originalSplit.name?.toLowerCase(), ocrItemsNames) : undefined
        const ocrBestMatchingName = ocrBestMatching?.bestMatch?.target?.toLowerCase()
        const ocrBestMatchingRating = ocrBestMatching?.bestMatch?.rating ?? 0

        for (const ocrItem of ocrItems) {
            const areSplitsMatching = ocrItem?.name?.toLowerCase() === ocrBestMatchingName && ocrBestMatchingRating >= 0.6

            // if the OCR split is matching with an original split, they should be compared
            if (areSplitsMatching) {
                const itemParent = compareSplitsData({ field: "itemParent", originalSplit, ocrItem, newCurrency, originalCurrency, itemIndex, t })

                const itemChildren: Array<RowInterface> = []

                for (const field of itemFieldsToBeCompared) {
                    const comparedData = compareSplitsData({ field, originalSplit, ocrItem, newCurrency, originalCurrency, itemIndex, t })
                    if (comparedData) itemChildren.push(comparedData)
                }

                if (itemChildren.length) {
                    if (itemParent) item = { ...itemParent, children: itemChildren }
                }
            }
        }

        return { ...item }
    })

    return splitsData
}

const compareSplitsData = ({ field, originalSplit, ocrItem, newCurrency, originalCurrency, itemIndex, t }: CompareSplitsDataParams): RowInterface | void => {
    let originalData: any = originalSplit[field as keyof ExpenseSplit]
    let ocrData: any = ocrItem[field as keyof ExpenseSplit]

    if (field === "itemParent") {
        originalData = "name" in originalSplit ? originalSplit.name : ""
        ocrData = ""
    }

    if (field === "taxRate") {
        ocrData = getExpenseTaxRate(ocrItem.taxRate)
        originalData = getExpenseTaxRate(originalSplit?.taxRate)
        if (!originalData) return
    }

    const formattedOcrData = formatFieldValue(field, ocrData, newCurrency)
    const formattedOriginalData = formatFieldValue(field, originalData, originalCurrency)
    const formattedField = t(`info:ocr_updates.fields_names.${field}`)

    const isDifferent = compareOcrAndOriginalData(formattedOriginalData, formattedOcrData)

    if (originalData !== undefined && ocrData !== undefined && isDifferent) {
        return {
            key: itemIndex + field,
            field,
            ocrData,
            itemIndex,
            formattedOriginalData,
            formattedOcrData,
            formattedField,
            split: true,
        }
    }
}

const compareData = (
    { field, expense, ocrObject, vendors, newCurrency, originalCurrency, t, isHospitality }: CompareDataParams,
    userLanguage: UserLanguageEnum | undefined,
): RowInterface | void => {
    let originalData = expense[field as keyof Expense]
    let ocrData: any = ocrObject[field as keyof OcrResult]
    let formattedField = t(`info:ocr_updates.fields_names.${field}`)

    if (field === "performancePeriod") {
        originalData = originalData || [expense.performancePeriodStartDate, expense.performancePeriodEndDate]
    }

    if (field === "datePurchased") {
        formattedField = t(`info:ocr_updates.fields_names.${isHospitality ? "hospitalityDate" : "performanceDate"}`)
    }

    if (field === "vendor") {
        const expenseCreditorName = expense.getCreditorName()
        const foundVendor = ocrObject.vendor?.name ? checkVendorSimilarity(vendors, ocrObject.vendor?.name) : false

        if (expenseCreditorName) originalData = { name: expenseCreditorName }
        if (foundVendor) ocrData = { name: foundVendor.name }
    }

    if (field === "merchant") {
        ocrData = ocrData || ocrObject.vendor?.name
    }

    if (field === "taxRate") {
        // if the tax rate of all items is the same and this tax rate already exists
        // the tax rate should be displayed in the modal, else "-" should be displayed
        const firstSplitTaxRate = ocrObject.splits && ocrObject.splits?.[0]?.taxRate?.taxRate

        const isItemsTaxRateSame = ocrObject.splits && ocrObject.splits?.length > 0 ? ocrObject.splits.every((s) => s.taxRate?.taxRate === firstSplitTaxRate) : true

        const taxByTaxRate = getExpenseTaxRate(ocrObject.taxRate)

        const shouldDisplayTotalTaxRate = isItemsTaxRateSame && taxByTaxRate

        const expenseTaxRate = getExpenseTaxRate(expense.taxRate)

        originalData = expenseTaxRate
        ocrData = shouldDisplayTotalTaxRate ? taxByTaxRate : { _id: NO_TAX_RATE }
    }

    const formattedOcrData = formatFieldValue(field, ocrData, newCurrency, userLanguage)
    const formattedOriginalData = formatFieldValue(field, originalData, originalCurrency, userLanguage)

    const isDifferent = compareOcrAndOriginalData(formattedOriginalData, formattedOcrData)

    if (isSet(ocrData) && isDifferent) {
        return {
            key: field,
            field,
            ocrData,
            formattedOriginalData,
            formattedOcrData,
            formattedField,
        }
    }
}

export const compareFormOcrData = ({ expense, ocrObject, vendors, t }: CompareFormOcrDataParams, loggedInUserLanguage: UserLanguageEnum) => {
    const originalCurrency = expense.currency ?? CurrencyEnum.EUR
    const newCurrency = ocrObject.currency ?? originalCurrency

    const isAutoGeneratedCardRequest = expense.isAutoGenerated && expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD
    const isHospitality = isHospitalityExpense(expense)

    // Set the invoice and vendor fields that need to be compared
    const invoiceAndVendorFieldsToBeCompared: Array<OcrObjectKeys> = ["invoiceNumber", "currency", "invoiceDate", "invoiceDueDate", "vendor", "performancePeriod", "datePurchased"]

    // For autoGenerated card requests we disregard the OCR amount prices and tax rate
    if (!isAutoGeneratedCardRequest) {
        invoiceAndVendorFieldsToBeCompared.push("totalNetPrice", "totalTaxPrice", "totalGrossPrice", "taxRate")
    }
    if (isHospitality) {
        invoiceAndVendorFieldsToBeCompared.push("merchant")
    }

    const formOCRDataDifferences: Array<RowInterface> = []

    const originalSplits = expense.splits
    const ocrItems = ocrObject.splits as Array<ItemSplit>

    const shouldUpdateItems = ocrItems && expense.kind === (isHospitality ? ExpenseKindEnum.HOSPITALITY : ExpenseKindEnum.ONE_TIME_EXPENSE)

    if (shouldUpdateItems) {
        const splits = compareSplits({ originalSplits, ocrItems, t, newCurrency, originalCurrency }).filter((item) => !isEmptyObject(item))
        if (splits?.length) {
            formOCRDataDifferences.push(...splits)
        }
    }

    for (const field of invoiceAndVendorFieldsToBeCompared) {
        const comparedData = compareData({ field, expense, ocrObject, vendors, newCurrency, originalCurrency, t, isHospitality }, loggedInUserLanguage)
        if (comparedData) formOCRDataDifferences.push(comparedData)
    }

    if (formOCRDataDifferences.length === 0) return

    const sortedDataDifference = formOCRDataDifferences.sort((a, _b) => (a.field === "itemParent" ? 1 : -1))

    return sortedDataDifference
}

export const mergeExistingAndOcrItems = (existingItems: Array<ExpenseSplit>, ocrItems: Array<OcrResultItem>) => {
    const noEmptyExistingItems = existingItems.filter((item) => item.netPrice !== 0)
    const expenseItemsNames = noEmptyExistingItems.map((split: ItemSplit) => split.name?.toLowerCase())
    const notRepeatedOcrDataItems = ocrItems.filter((item: ItemSplit) => !expenseItemsNames.includes(item.name?.toLowerCase()))

    return [...noEmptyExistingItems, ...notRepeatedOcrDataItems]
}

const getExpenseTaxRate = (taxRate?: Partial<ExtendedReferencedTax>): ExtendedReferencedTax => {
    if (taxRate?._id) {
        const newTaxRate = TaxService.getTaxById(taxRate._id)
        if (newTaxRate) return newTaxRate
    }

    if (taxRate?.taxRate || taxRate?.taxRate === 0) {
        const newTaxRate = TaxService.getTaxByTaxRate(taxRate?.taxRate)
        if (newTaxRate) return newTaxRate
    }

    return {
        _id: NO_TAX_RATE,
    }
}

const compareOcrAndOriginalData = (originalData: string, ocrData: string) => originalData?.toLowerCase() !== ocrData?.toLowerCase()

const updateOcrItems = ({ ocrItemizationEnabled, ocrObject, expense, taxes, t, isHospitality }: UpdateOcrItemsParams): SplitDataType => {
    const shouldUpdateItems = ocrItemizationEnabled && ocrObject.splits && expense.kind === (isHospitality ? ExpenseKindEnum.HOSPITALITY : ExpenseKindEnum.ONE_TIME_EXPENSE)

    if (shouldUpdateItems) {
        return {
            splitType: SplitTypeEnum.ITEM,
            splits: ocrObject.splits?.map((itemSplit: ItemSplit) => ({
                ...itemSplit,
                name: itemSplit.name ?? "Item",
                taxRate: getExpenseTaxRate(itemSplit.taxRate),
            })),
        }
    }

    if (expense.splitType === SplitTypeEnum.ITEM) {
        return undefined
    }

    return {
        splits: expense.splits?.map((split: InvoiceSplit) => ({
            ...split,
            netPrice: (ocrObject.totalNetPrice ?? expense.totalNetPrice ?? 0) * (split.percentage / 100),
            taxPrice: (ocrObject.totalTaxPrice ?? expense.totalTaxPrice ?? 0) * (split.percentage / 100),
            taxRate: ocrObject.taxRate ? getExpenseTaxRate(ocrObject.taxRate) : expense.taxRate ? expense.taxRate : { _id: NO_TAX_RATE },
            grossPrice: (ocrObject.totalGrossPrice ?? expense.totalGrossPrice ?? 0) * (split.percentage / 100),
        })),
    }
}

const updatingAttendees = (existingData: Expense | (Omit<Hospitality, "attendees"> & { attendees: Array<LabeledValue> }), requester?: Employee) => {
    const areExistingAttendees = "attendees" in existingData && existingData.attendees?.length > 0

    if (!areExistingAttendees && !requester) return

    if (areExistingAttendees && !requester) return existingData.attendees

    if (requester) {
        const requesterObject = { value: requester.id, label: requester.name }

        if (areExistingAttendees) {
            const containsRequester = existingData.attendees.find(({ value }) => value === requesterObject.value)
            return containsRequester ? existingData.attendees : [...existingData.attendees, requesterObject]
        }

        return [requesterObject]
    }
}

export const onUpdateExpenseFromOcr = async ({
    updatedOcrObject,
    activeExpense,
    vendors,
    taxes,
    t,
    isOcrItemizationEnabled,
    company,
    expense,
    dispatch,
}: OnUpdateExpenseFromOcrParams) => {
    try {
        const updateData = buildOcrUpdateData({ ocrObject: updatedOcrObject, existingData: activeExpense, vendors, taxes, t, ocrItemizationEnabled: isOcrItemizationEnabled })
        deleteUnnecessaryExpenseFormData(updateData)

        // check for vendor rule to adjust dueOn if needed
        const vendor = activeExpense.vendor?._id ? VendorService.getVendorById(activeExpense.vendor._id) : undefined
        const paymentTerm = vendor?.rule?.paymentTerm
        const dueDateUpdate = !updateData.invoiceDueDate && paymentTerm && updateData.invoiceDate ? { invoiceDueDate: moment(updateData.invoiceDate).add(paymentTerm, "days") } : {}
        const newExpense = new Expense({ ...activeExpense, ...updateData, ...dueDateUpdate, skipEmail: true, isOcrUpdate: true })
        const updatedExpense = syncExpenseSplits(newExpense, newExpense.splitType)
        // Rebuild approval process with ocr changes. Expenses inside folder do not change its aproval processes and follow the folder approval processes.
        if (!updatedExpense.folderId) {
            const approvalProcesses = await getApprovalProcessesAfterExpenseUpdate(updatedExpense, company, {
                previousExpense: expense,
                awaitConfirmation: true,
                ocrConfirmation: true,
            })
            if (!approvalProcesses) return
            updatedExpense.approvalProcesses = approvalProcesses
        } else {
            const folder = expense as TripFolder
            const isFolderAmountChangeApproved = await confirmFolderAmountChange(updatedExpense.totalGrossPrice - activeExpense.totalGrossPrice, folder, company)
            if (!isFolderAmountChangeApproved) return
        }

        await updateExpense(updatedExpense.id, updatedExpense)(dispatch)
        NotificationService.send(NotificationTypeEnum.INFO, t("notification:ocr.processed.title"), t("notification:ocr.processed_and_updated.message"), 60)
    } catch (err) {
        NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:ocr.title"))
    }
}
